import { useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import ApartmentIcon from '@mui/icons-material/Apartment';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useTranslation } from 'react-i18next';
import { NotificationBell } from '../components/NotificationBell';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Select, SelectOption } from '../components/ui/Select';
import { useAuth } from '../auth/AuthContext';
import { hasPermission, isInternalUser } from '../auth/permissions';
import { colorTokens, layoutTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { CustomerPortalProvider, useCustomerPortal } from '../portal/CustomerPortalContext';
import mafertSystemLogoWhite from '../assets/brand/mafert-system-logo-white.svg';
import {
  sidebarNavIconSx,
  sidebarNavItemSx,
  sidebarNavTextProps,
} from './sidebarNavStyles';

const { semantic } = colorTokens;

/** Customer portal content rail (calmer than internal data-list pages). */
const PORTAL_CONTENT_MAX_WIDTH = 1008;

const getNavItems = (t) => [
  { label: t('navigation:portal_dashboard'), path: '/portal', icon: <DashboardIcon fontSize="small" /> },
  { label: t('navigation:customers'), path: '/portal/customers', icon: <BusinessIcon fontSize="small" />, permission: 'customer.company.view' },
  { label: t('navigation:portal_tenants'), path: '/portal/tenants', icon: <ApartmentIcon fontSize="small" />, permission: 'customer.tenants.view' },
  { label: t('navigation:portal_support'), path: '/portal/support', icon: <SupportAgentIcon fontSize="small" />, permission: 'customer.support.view' },
  { label: t('navigation:portal_billing'), path: '/portal/billing', icon: <ReceiptIcon fontSize="small" />, permission: 'customer.billing.view' },
  { label: t('navigation:portal_my_modules'), path: '/portal/entitlements', icon: <ToggleOnIcon fontSize="small" />, permission: 'customer.billing.view' },
  { label: t('navigation:portal_profile'), path: '/portal/profile', icon: <AccountCircleIcon fontSize="small" /> },
];

function isNavSelected(pathname, itemPath) {
  if (itemPath === '/portal') {
    return pathname === '/portal';
  }

  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function PortalLayoutInner() {
  const { t } = useTranslation(['navigation', 'common']);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { session, logout } = useAuth();
  const { customers, activeCustomerId, setActiveCustomerId } = useCustomerPortal();
  const location = useLocation();
  const navigate = useNavigate();

  const isInternalSupport = isInternalUser(session);
  const navItems = getNavItems(t);
  const visibleNav = navItems.filter((item) => {
    if (isInternalSupport) {
      return true;
    }

    return !item.permission || hasPermission(session, item.permission, activeCustomerId);
  });

  const topbarActionSx = {
    width: 40,
    height: 40,
    borderRadius: `${radiusTokens.iconButton}px`,
    border: `1px solid ${semantic.border.default}`,
    bgcolor: semantic.action.secondary.bg,
    color: semantic.text.primary,
    '&:hover': {
      bgcolor: semantic.bg.surface,
    },
  };

  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: semantic.bg.sidebar,
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.75, boxSizing: 'border-box', flexShrink: 0 }}>
        <Typography
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 14,
            lineHeight: '20px',
            color: semantic.text.primary,
          }}
        >
          {t('navigation:customer_portal')}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: semantic.border.subtle }} />

      {customers.length > 1 && (
        <Box
          sx={{
            px: 2,
            pt: 2.5,
            pb: 2.5,
            boxSizing: 'border-box',
          }}
        >
          <Select
            density="Compact"
            fullWidth
            label={t('navigation:company')}
            value={activeCustomerId || ''}
            onChange={(e) => setActiveCustomerId(Number(e.target.value))}
          >
            {customers.map((customer) => (
              <SelectOption key={customer.id} value={customer.id}>
                {customer.name}
              </SelectOption>
            ))}
          </Select>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 1.5,
          py: 1,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            width: 0,
            height: 0,
          },
        }}
      >
        <List disablePadding>
          {visibleNav.map((item) => {
            const selected = isNavSelected(location.pathname, item.path);

            return (
              <ListItemButton
                key={item.path}
                component={RouterLink}
                to={item.path}
                selected={selected}
                onClick={() => setMobileOpen(false)}
                sx={sidebarNavItemSx({ selected })}
              >
                <ListItemIcon sx={sidebarNavIconSx({ selected })}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={sidebarNavTextProps({
                    selected,
                    fontSize: 13,
                  })}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login', { replace: true });
  };

  const drawerPaperSx = {
    width: layoutTokens.sidebarWidth,
    boxSizing: 'border-box',
    bgcolor: semantic.bg.sidebar,
    backgroundImage: 'none',
    borderRight: `1px solid ${semantic.border.subtle}`,
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: semantic.bg.canvas }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: semantic.bg.topbar,
          backgroundImage: 'none',
          borderBottom: `1px solid ${semantic.border.subtle}`,
          color: semantic.text.primary,
        }}
      >
        <Toolbar
          sx={{
            gap: 1.5,
            px: { xs: 2, md: 3 },
            minHeight: `${layoutTokens.topbarHeight}px !important`,
          }}
        >
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ ...topbarActionSx, display: { sm: 'none' } }}
            aria-label="Open navigation"
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src={mafertSystemLogoWhite}
              alt="Mafert System"
              sx={{
                width: 120,
                maxWidth: '100%',
                maxHeight: 36,
                height: 'auto',
                objectFit: 'contain',
                objectPosition: 'left center',
                display: 'block',
              }}
            />
          </Box>
          {isInternalSupport && (
            <Typography
              sx={{
                mr: 1,
                display: { xs: 'none', md: 'block' },
                fontSize: 12,
                letterSpacing: '0.24px',
                color: semantic.text.muted,
              }}
            >
              {t('navigation:support_mode')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ '& .MuiIconButton-root': topbarActionSx }}>
              <NotificationBell
                notificationsPath="/portal/notifications"
                preferencesPath="/portal/notification-preferences"
              />
            </Box>
            <Box sx={{ '& .MuiIconButton-root': topbarActionSx }}>
              <LanguageSwitcher persistToApi />
            </Box>
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={topbarActionSx}
              aria-label={t('common:profile')}
            >
              <AccountCircleIcon fontSize="small" />
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 220,
                bgcolor: semantic.bg.surface,
                backgroundImage: 'none',
                border: `1px solid ${semantic.border.subtle}`,
                borderRadius: `${radiusTokens.md}px`,
                '& .MuiMenuItem-root': {
                  fontFamily: typographyTokens.fontFamily,
                  fontSize: 13,
                  color: semantic.text.secondary,
                  minHeight: 36,
                  '&:hover': {
                    bgcolor: semantic.action.secondary.bg,
                    color: semantic.text.primary,
                  },
                },
              },
            }}
          >
            <MenuItem disabled>
              <Typography
                sx={{
                  fontSize: 12,
                  color: semantic.text.muted,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {session?.user?.email}
              </Typography>
            </MenuItem>
            {isInternalSupport && (
              <MenuItem component={RouterLink} to="/" onClick={() => setAnchorEl(null)}>
                {t('navigation:admin_dashboard')}
              </MenuItem>
            )}
            <MenuItem component={RouterLink} to="/portal/profile" onClick={() => setAnchorEl(null)}>
              {t('navigation:portal_profile')}
            </MenuItem>
            <MenuItem component={RouterLink} to="/portal/account/security" onClick={() => setAnchorEl(null)}>
              {t('navigation:account_security')}
            </MenuItem>
            <MenuItem onClick={handleLogout}>{t('common:logout')}</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: layoutTokens.sidebarWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              ...drawerPaperSx,
              top: layoutTokens.topbarHeight,
              height: `calc(100% - ${layoutTokens.topbarHeight}px)`,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${layoutTokens.sidebarWidth}px)` },
          minWidth: 0,
          minHeight: '100vh',
          bgcolor: semantic.bg.canvas,
          pt: `${layoutTokens.topbarHeight}px`,
        }}
      >
        <Box
          sx={{
            maxWidth: PORTAL_CONTENT_MAX_WIDTH,
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 4 },
            py: 3,
            boxSizing: 'border-box',
          }}
        >
          {isInternalSupport && (
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              {t('navigation:support_mode_banner')}
            </Alert>
          )}
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export function CustomerPortalLayout() {
  return (
    <CustomerPortalProvider>
      <PortalLayoutInner />
    </CustomerPortalProvider>
  );
}
